import { useStaticQuery, graphql } from 'gatsby';

import type {
  ICmsSite,
  IUniversalAlertBar,
  IUniversalMenuData,
  IUniversalMenuItemCopyright,
  IUniversalMenuItemCTA,
  IUniversalMenuItemWithCustomFields,
} from '../types';


export const useUniversalNavigationQuery = (args?: { multiLocale?: boolean; }) => {
  const { multiLocale = false } = args || {};

  const data = useStaticQuery(graphql`
    fragment CmsSiteFragment on CMSSite {
      databaseId
      hreflang
      id
      includeInHfrelangTags
      isoCode
      isoName
      locale
      name
      nativeName
      path
    }

    fragment UniversalAlertBarFragment on UniversalAlertBar {
      backgroundColor
      backgroundImage
      enabled
      excludedPaths
      href
      icon
      id
      locale
      message
      openInNewTab
      showIcon
      textColor
    }

    fragment UniversalMenuItemCustomFieldsFragment on UniversalMenuItem {
      badge {
        color
        text
      }
      callToAction {
        text
        url
      }
      eyebrow
      featured
      iconName
      image {
        altText
        sourceUrl
      }
      position
      trailingMark
      type
      width
    }
  
    fragment UniversalMenuItemFragment on UniversalMenuItem {
      description
      id
      label
      locale
      order
      target
      url
    }
  
    fragment UniversalMenuFragment on UniversalMenuItem {
      ...UniversalMenuItemFragment
      ...UniversalMenuItemCustomFieldsFragment
      childItems {
        ...UniversalMenuItemFragment
        ...UniversalMenuItemCustomFieldsFragment
        childItems {
          ...UniversalMenuItemFragment
          ...UniversalMenuItemCustomFieldsFragment
        }
      }
    }
  
    query UniversalNavigation {
      meta: allUniversalMenuItem {
        locales: distinct(field: {locale: SELECT})
      }
      sites: allCmsSite {
        nodes {
          ...CmsSiteFragment
        }
      }
      academy: allUniversalMenuItem(filter: {locations: {eq: "ACADEMY"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuFragment
        }
      }
      alertBar: allUniversalAlertBar {
        nodes {
          ...UniversalAlertBarFragment
        }
      }
      copyright: allUniversalMenuItem(filter: {locations: {eq: "UNIVERSAL_COPYRIGHT"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuItemFragment
        }
      }
      ctas: allUniversalMenuItem(filter: {locations: {eq: "UNIVERSAL_CTAS"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuItemFragment
          variant
        }
      }
      ctasLoggedIn: allUniversalMenuItem(filter: {locations: {eq: "UNIVERSAL_CTAS_LOGGED_IN"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuItemFragment
          variant
        }
      }
      footer: allUniversalMenuItem(filter: {locations: {eq: "UNIVERSAL_FOOTER"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuFragment
        }
      }
      header: allUniversalMenuItem(filter: {locations: {eq: "UNIVERSAL_NAVIGATION"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuFragment
        }
      }
      helpCenter: allUniversalMenuItem(filter: {locations: {eq: "HELP_CENTER"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuFragment
        }
      }
      utility: allUniversalMenuItem(filter: {locations: {eq: "UTILITY"}, parentId: {eq: null}}, sort: {order: ASC}) {
        nodes {
          ...UniversalMenuItemFragment
          childItems {
            ...UniversalMenuItemFragment
          }
        }
      }
    }
  `);

  const {
    meta: { locales = [] },
    sites: { nodes: siteItems = [] },
    academy: { nodes: academyItems = [] },
    alertBar: { nodes: alertBarItems = [] },
    copyright: { nodes: copyrightItems = [] },
    ctas: { nodes: ctaItems = [] },
    ctasLoggedIn: { nodes: ctaItemsLoggedIn = [] },
    footer: { nodes: footerItems = [] },
    header: { nodes: headerItems = [] },
    helpCenter: { nodes: helpCenterItems = [] },
    utility: { nodes: utilityItems = [] },
  }: {
    meta: { locales: string[]; };
    sites: { nodes: ICmsSite[]; };
    academy: { nodes: IUniversalMenuItemWithCustomFields[]; };
    alertBar: { nodes: IUniversalAlertBar[]; };
    copyright: { nodes: IUniversalMenuItemCopyright[]; };
    ctas: { nodes: IUniversalMenuItemCTA[]; };
    ctasLoggedIn: { nodes: IUniversalMenuItemCTA[]; };
    footer: { nodes: IUniversalMenuItemWithCustomFields[]; };
    header: { nodes: IUniversalMenuItemWithCustomFields[]; };
    helpCenter: { nodes: IUniversalMenuItemWithCustomFields[]; };
    utility: { nodes: IUniversalMenuItemWithCustomFields[]; };
  } = data;

  if (multiLocale === false) return {
    academy: academyItems,
    alertBar: alertBarItems[0] || null,
    copyright: copyrightItems,
    ctas: ctaItems,
    ctasLoggedIn: ctaItemsLoggedIn,
    footer: footerItems,
    header: headerItems,
    helpCenter: helpCenterItems,
    sites: siteItems,
    utility: utilityItems,
  };

  if (locales.length >= 1) {
    const localeData: {
      [x: string]: IUniversalMenuData;
    } = {};

    locales.forEach((locale: string) => {
      localeData[locale] = {
        academy: academyItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        alertBar: alertBarItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale)[0] || null,
        copyright: copyrightItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        ctas: ctaItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        ctasLoggedIn: ctaItemsLoggedIn.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        footer: footerItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        header: headerItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        helpCenter: helpCenterItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
        sites: siteItems,
        utility: utilityItems.filter(({ locale: itemLocale }: { locale: string; }) => itemLocale === locale),
      };
    });

    return localeData;
  }

  return;
};
